import anime from 'animejs'

/**
 * Animation helpers required for interactions on desktop version of the people page feature.
 * Utilises FLIP animation technique to implement shared element transitions.
 */

const heightAnimationSpeed = 250
const fadeAnimationSpeed = 400

/**
 * Given image tile element, query all required elements and return as one object
 * @param {Element} itemEl
 * @returns {Object}
 */
const getAnimationEls = (itemEl) => {
  return {
    tile: itemEl.querySelector('.js-profile-image-tile'),
    profile: itemEl.querySelector('.js-profile-desktop'),
  }
}

export const expandProfile = (itemEl) => {
  const target = getAnimationEls(itemEl)
  const featureRootEl = itemEl.closest('.js-people-feature')

  target.profile.classList.add('c-profile--animating-in')
  const gridSection = target.tile.closest('.c-gallery-feature__grid-section')
  const gridSectionHeight = gridSection ? gridSection.getBoundingClientRect().height : 0
  const profileHeight = target.profile.getBoundingClientRect().height

  // If profile height taller than containing block, expand block first
  if (profileHeight > gridSectionHeight) {
    if (!featureRootEl.classList.contains('js-expand-pending')) {
      gridSection.dataset.height = gridSection.getBoundingClientRect().height
    }
    anime({
      targets: gridSection,
      easing: 'easeInOutQuad',
      duration: heightAnimationSpeed,
      height: profileHeight + 2,
      complete: () => {
        fadeInProfile(target.profile, featureRootEl)
      },
    })
  } else {
    fadeInProfile(target.profile, featureRootEl)
  }
}

const fadeInProfile = (profile, featureRootEl) => {
  profile.classList.remove('c-profile--animating-in')
  profile.classList.add('c-profile--active')
  anime({
    targets: profile,
    easing: 'linear',
    duration: fadeAnimationSpeed,
    opacity: [0, 1],
    complete: () => {
      featureRootEl.scrollIntoView({ behavior: 'smooth' })
    },
  })
}

/**
 * Animates a profile closed with opacity fade and optional height contraction.
 *
 * Handles two scenarios:
 * 1. Quick transition (profile-to-profile): Only fades opacity, no height change
 * 2. Full transition (profile-to-grid): Fades opacity and contracts height
 *
 * The returned Promise resolves with different values to indicate which animation * occurred, allowing calling fns to conditionally handle post-animation logic.
 *
 * @param {*} itemEl - The profile item element to contract
 * @returns {Promise<string>} Resolves to 'no-height-change' or 'height-animated'
 */
export const contractProfile = (itemEl) => {
  return new Promise((resolve) => {
    const target = getAnimationEls(itemEl)
    const gridSection = target.tile.closest('.c-gallery-feature__grid-section')
    const featureRootEl = itemEl.closest('.js-people-feature')

    anime({
      targets: target.profile,
      easing: 'linear',
      duration: fadeAnimationSpeed,
      opacity: [1, 0],
      complete: () => {
        target.profile.classList.remove('c-profile--active')
        if (itemEl.closest('.js-people-feature').classList.contains('js-expand-pending')) {
          itemEl.closest('.js-people-feature').classList.remove('js-expand-pending')
          resolve('no-height-change')
        } else {
          anime({
            targets: gridSection,
            easing: 'easeInOutQuad',
            duration: heightAnimationSpeed,
            height: gridSection.dataset.height,
            complete: () => {
              gridSection.dataset.height = ''
              gridSection.style = ''
              resolve('height-animated')
            },
          })
        }
        featureRootEl.scrollIntoView({ behavior: 'smooth' })
      },
    })
  })
}
